import { useTranslation } from "react-i18next"
import { AdvancedSettingWrapper } from "./styles"
import CustomButton from "@Common/CustomButton"
import { useRecoilState, useRecoilValue } from "recoil"
import { PopupOverlayNewCalender } from "@store/index"
import PricingTab from "./pricingTab"
import AvailabilityTab from "./availabilityTab"
import { useEffect, useMemo, useState } from "react"
import { useGetListingManagement, useCategoryMangment } from "@services"
import type { CategoryGroup } from "./GroupedPropertySelect"

function CategoryDetailFetcher({
	categoryId,
	onData,
}: {
	categoryId: number
	onData: (catId: number, propertyIds: number[]) => void
}) {
	const { GetCategoryByIdQuery } = useCategoryMangment()
	const { data } = GetCategoryByIdQuery(categoryId.toString())
	useEffect(() => {
		const result = data?.data?.result
		if (!result?.length) return
		const ids = (result[0]?.properties ?? []).map(
			(p: { propertyId: number }) => p.propertyId
		)
		onData(categoryId, ids)
	}, [data, categoryId])
	return null
}

export default function AdvancedSettings() {
	/* ------------------------------- Local state ------------------------------ */
	const [step, setActiveStep] = useState(1)
	const [categoryPropertyMap, setCategoryPropertyMap] = useState<Map<number, number[]>>(new Map())
	const { t } = useTranslation()
	const [isPopupOverlayNewCalender, setPopupOverlayNewCalender] =
		useRecoilState(PopupOverlayNewCalender)
	/* ---------------------------------- API'S --------------------------------- */
	const { data: getListingManagement } = useGetListingManagement()
	const { GetCategoriesQuery } = useCategoryMangment()
	const { data: categoriesData } = GetCategoriesQuery({ pageNumber: "1", pageSize: "100" })
	const categories = categoriesData?.data?.result ?? []

	const selectedPropertyId = isPopupOverlayNewCalender?.propertyValue?.value

	const sortedCategoryGroups = useMemo<CategoryGroup[]>(() => {
		const groups: CategoryGroup[] = categories
			.map((cat) => ({
				categoryId: cat.categoryId,
				categoryName: cat.categoryName,
				propertyIds: categoryPropertyMap.get(cat.categoryId) ?? [],
			}))
			.filter((g) => g.propertyIds.length > 0)

		if (!selectedPropertyId) return groups
		const selId = Number(selectedPropertyId)
		const selIdx = groups.findIndex((g) => g.propertyIds.includes(selId))
		if (selIdx <= 0) return groups
		const sorted = [...groups]
		const [selGroup] = sorted.splice(selIdx, 1)
		sorted.unshift(selGroup)
		return sorted
	}, [categories, categoryPropertyMap, selectedPropertyId])

	const handleCategoryData = useMemo(
		() => (catId: number, ids: number[]) => {
			setCategoryPropertyMap((prev) => {
				if (JSON.stringify(prev.get(catId)) === JSON.stringify(ids)) return prev
				const next = new Map(prev)
				next.set(catId, ids)
				return next
			})
		},
		[]
	)

	useEffect(() => {
		if (!isPopupOverlayNewCalender?.isAvailable) {
			setActiveStep(2)
		}
	}, [isPopupOverlayNewCalender])

	return (
		<AdvancedSettingWrapper>
			{/* Fetch property IDs for each category (React Query caches these) */}
			{categories.map((cat) => (
				<CategoryDetailFetcher
					key={cat.categoryId}
					categoryId={cat.categoryId}
					onData={handleCategoryData}
				/>
			))}

			<div className='HeaderSection'>
				<div className='title'>
					<p>{t("CALENDAR.ADVANCEDSETTING")}</p>
				</div>
				<div className='containerButton'>
					<CustomButton
						label={t("SAVE")}
						withBG
						customPadding='7px 21px'
						form={step === 1 ? "pricingForm" : "availabilityForm"}
						type='submit'
					/>
					<CustomButton
						label={t("GENERAL.CANCEL_ALT")}
						customPadding='7px 21px'
						customColor='#E85B5B'
						customBG='#E85B5B17'
						borderColor='#E85B5B17'
						onClick={() => setPopupOverlayNewCalender(undefined)}
					/>
				</div>
			</div>

			<div className='containerTabSection'>
				<button
					type='button'
					className={step === 1 ? "active" : ""}
					onClick={() => setActiveStep(1)}
				>
					{t("COMMON.PRICING")}
				</button>
				<button
					type='button'
					className={step === 2 ? "active" : ""}
					onClick={() => setActiveStep(2)}
				>
					{t("CALENDAR.AVAILABILITY")}
				</button>
			</div>
			{step === 1 && (
				<PricingTab
					getListingManagement={getListingManagement}
					categoryGroups={sortedCategoryGroups}
				/>
			)}
			{step === 2 && (
				<AvailabilityTab
					getListingManagement={getListingManagement}
					categoryGroups={sortedCategoryGroups}
				/>
			)}
		</AdvancedSettingWrapper>
	)
}
